import React from "react";

interface BadgeButtonProps {
  value: {
    text: string;
    color?: string;
    bgColor?: string;
    displayText?: string;
    showInfoIcon?: boolean;
    hoverText?: string;
    isDisabled?: boolean;
  };
  onReviewClick?: () => void;
  [key: string]: any;
}

/**
 * Renders a badge-styled control that can act as a button (optional `onReviewClick`).
 * Omits the native `title` tooltip when `hoverText` / `displayText` are empty so hovering does not show a redundant hint.
 * Uses `pointer` when enabled and `default` when disabled (no “not allowed” cursor).
 *
 * @param value - Label, colors, optional tooltip fields, and disabled state
 * @param onReviewClick - Optional handler when the badge is clicked and not disabled
 * @returns Badge button markup
 */
export const BadgeButton: React.FC<BadgeButtonProps> = ({
  value,
  onReviewClick,
}) => {
  const handleReviewClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event?.preventDefault();
    if (value.isDisabled) {
      event.stopPropagation();
      return;
    }
    if (onReviewClick) {
      event.stopPropagation();
      onReviewClick();
    }
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (value.isDisabled) {
      event.stopPropagation();
    }
  };

  const showOpacity =
    value.isDisabled ||
    value.text === "Not Started" ||
    value.text === "Not Sent";

  const rawTitle = !value.showInfoIcon
    ? value.hoverText || value.displayText
    : value.displayText;
  const titleAttr =
    typeof rawTitle === "string" && rawTitle.trim() !== ""
      ? rawTitle
      : undefined;

  return (
    <div
      className={`table-cell-badge ${showOpacity ? "opacity-50" : ""}`}
      style={{
        color: value.color || "#000000",
        backgroundColor: value.bgColor || "#F5F5F5",
        cursor: value.isDisabled ? "default" : "pointer",
      }}
      title={titleAttr}
      onClick={handleReviewClick}
      onMouseDown={handleMouseDown}
    >
      {value.text}
    </div>
  );
};

export default BadgeButton;
